class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.wrapper = null;
    this.content = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this.wrapper = this.querySelector('.marquee__wrapper');
    this.content = this.querySelector('.marquee__content');

    if (!this.wrapper || !this.content) return;

    // Pause when off-screen
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => this._setOffscreenPause(!entry.isIntersecting),
      { threshold: 0 }
    );
    this.intersectionObserver.observe(this);

    // Reduced motion
    this._mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._onMqChange = (e) => this.toggleAttribute('data-disabled', e.matches);
    this.toggleAttribute('data-disabled', this._mq.matches);
    this._mq.addEventListener('change', this._onMqChange);

    // Wait for fonts so scrollWidth is accurate, then set up clones
    const setup = () => this._setup();
    document.fonts ? document.fonts.ready.then(setup) : requestAnimationFrame(setup);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this._mq?.removeEventListener('change', this._onMqChange);
  }

  _setup() {
    // Remove previously added clones
    this.wrapper.querySelectorAll('[aria-hidden="true"]').forEach((el) => el.remove());

    const gap = parseFloat(getComputedStyle(this.wrapper).gap) || 0;
    const contentWidth = this.content.scrollWidth;
    const componentWidth = this.offsetWidth;

    // Add enough clones so content fills at least 2× the component width
    const clonesNeeded = Math.max(1, Math.ceil((2 * componentWidth) / contentWidth));
    for (let i = 0; i < clonesNeeded; i++) {
      const clone = this.content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.wrapper.appendChild(clone);
    }

    // Translate by exactly one content-set width (content + one gap) for seamless loop
    this.style.setProperty('--marquee-translate', `-${contentWidth + gap}px`);

    // Speed: pixels per second → duration scales with content width
    const speedFactor = parseFloat(this.dataset.speedFactor) || 25;
    this.style.setProperty('--marquee-speed', `${contentWidth / speedFactor}s`);

    // Recalculate on resize
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this._setup());
    this.resizeObserver.observe(this);
  }

  _setOffscreenPause(paused) {
    this.wrapper.style.animationPlayState = paused ? 'paused' : '';
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
